<template>
  <Popup :ref="(el) => popup = el"
         class="popup__xl"
         :title="helper.trans('labels.counterparty_statistics')"
         @onClose="hide"
  >
    <LoaderContent ref="loader" class="">
      <div class="" style="overflow: auto">
        <table class="table table-striped" style="position: relative">
          <thead>
          <tr>
            <th class="text-start p-sm">{{ helper.trans('labels.user_id') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.store_hash') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.use_count') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.completed_count') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.total_completed') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.total_use_count') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.conversion_rate') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.total_conversion_rate') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.average_check') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.days_since_last_use') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.is_banned') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.first_used_at') }}</th>
            <th class="text-start p-sm">{{ helper.trans('labels.last_used_at') }}</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in counterparties" :key="index" class="">
            <td class="p-sm">
              <div class="d-flex align-items-center gap-2">
                <span v-b-tooltip.hover :title="item.userId">{{ item.userId.substring(0, 8) }}...</span>
                <copy-button :value="item.userId"></copy-button>
              </div>
            </td>
            <td class="p-sm">
              <span v-b-tooltip.hover :title="item.storeHash">{{ item.storeHash ? item.storeHash.substring(0, 12) + '…' : '—' }}</span>
            </td>
            <td class="p-sm">{{ numberFormatter.formatNumber(item.useCount) }}</td>
            <td class="p-sm">{{ numberFormatter.formatNumber(item.completedCount) }}</td>
            <td class="p-sm">{{ numberFormatter.formatNumber(item.totalCompleted ?? item.completedCount) }}</td>
            <td class="p-sm">{{ item.totalUseCount != null ? numberFormatter.formatNumber(item.totalUseCount) : '—' }}</td>
            <td class="p-sm">{{ numberFormatter.formatNumber(item.conversionRate, 2) }}%</td>
            <td class="p-sm">{{ item.totalConversionRate != null ? numberFormatter.formatNumber(item.totalConversionRate, 2) + '%' : '—' }}</td>
            <td class="p-sm">{{ numberFormatter.formatNumber(Number.parseFloat(item.averageCheck), 2) }}</td>
            <td class="p-sm">{{ numberFormatter.formatNumber(item.daysSinceLastUse) }}</td>
            <td class="p-sm">
              <span v-if="item.isBanned" class="badge bg-danger">{{ helper.trans('labels.banned') }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td class="p-sm">
              <span v-if="item.firstUsedAt">{{ dateHelper.format(item.firstUsedAt, 'YYYY-MM-DD') }}</span>
              <span v-else class="text-muted">-</span>
            </td>
            <td class="p-sm">{{ dateHelper.format(item.lastUsedAt, 'YYYY-MM-DD') }}</td>
            <td class="text-end p-sm">
              <div class="d-flex gap-2 justify-content-end align-items-center">
                <button v-if="!item.isBanned && item.storeHash" type="button" @click="openBanPopup(item)" class="btn btn-sm btn-outline-danger text-nowrap text-capitalize-first">
                  {{ helper.trans('button.ban') }}
                </button>
                <button type="button" @click="handleReattachClick(item.userId)" class="btn btn-sm btn-outline-primary text-nowrap text-capitalize-first">
                  {{ helper.trans('labels.reattach') }}
                </button>
                <button type="button" @click="confirmDeleteCounterparty(item.userId)" class="btn btn-sm btn-outline-danger text-nowrap text-capitalize-first">
                  {{ helper.trans('button.unlink') }}
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="counterparties.length === 0">
            <td colspan="14" class="text-center py-5">
              <div style="display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 40px 20px;">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="opacity: 0.4;">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
                <div style="font-size: 16px; color: #6c757d; font-weight: 500;">
                  {{ helper.trans('labels.no_history') }}
                </div>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </LoaderContent>
    <ConfirmPopup ref="confirmPopup"></ConfirmPopup>
    <BanUserPopup ref="banUserPopup" @banned="onBanned" />
    <ReattachRequisitePopup 
      ref="reattachPopup" 
      @selected="handleReattach"
    ></ReattachRequisitePopup>
  </Popup>
</template>

<script lang="ts">
import {defineComponent, ref} from 'vue'
import {helper} from "@/service/CommonHelper";
import {api} from "@/service/APIClient";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import Popup from "@/apps/public/components/Popup.vue";
import LoaderContent from "@/apps/public/components/LoaderContent.vue";
import CounterpartyStatisticsDTO from "@/models/CounterpartyStatisticsDTO";
import ConfirmPopup from "@/apps/public/components/ConfirmPopup.vue";
import BanUserPopup from "@/apps/public/components/BanUserPopup.vue";
import {useNotification} from "@kyvg/vue3-notification";
import ReattachRequisitePopup from "@/apps/public/components/ReattachRequisitePopup.vue";
import CopyButton from "@/apps/public/components/CopyButton.vue";

const notification = useNotification();

export default defineComponent({
  components: {
    LoaderContent,
    Popup,
    ConfirmPopup,
    BanUserPopup,
    ReattachRequisitePopup,
    CopyButton,
  },
  setup() {
    const loader = ref<InstanceType<typeof LoaderContent> | null>(null)
    const confirmPopup = ref<InstanceType<typeof ConfirmPopup> | null>(null)
    const banUserPopup = ref<InstanceType<typeof BanUserPopup> | null>(null)
    const popup = ref<InstanceType<typeof Popup> | null>(null)
    const reattachPopup = ref<InstanceType<typeof ReattachRequisitePopup> | null>(null)
    const requisitesId = ref<number|null>(null)
    const counterparties = ref<CounterpartyStatisticsDTO[]>([])

    const show = async (id: number) => {
      requisitesId.value = id
      popup.value?.show()
      await load()
    }

    const hide = () => {
      popup.value?.hide()
      requisitesId.value = null
    }

    const load = async () => {
      if (!requisitesId.value) {
        return;
      }

      await loader.value?.load(async () => {
        try {
          counterparties.value = await api.requisitesCounterparties(requisitesId.value!)
        } catch (error) {
          counterparties.value = []
        }
      })
    }

    const confirmDeleteCounterparty = (userId: string) => {
      if (!requisitesId.value) {
        return;
      }

      confirmPopup.value?.show({
        title: helper.trans('labels.unlink_counterparty_confirm'),
        okTitle: helper.trans('button.unlink'),
        onOkClick: async () => {
          await deleteCounterparty(userId)
        }
      })
    }

    const deleteCounterparty = async (userId: string) => {
      if (!requisitesId.value) {
        return;
      }

      await loader.value?.load(async () => {
        try {
          await api.deleteRequisitesCounterparty(requisitesId.value!, userId)
          notification.notify({title: helper.trans('labels.counterparty_unlinked'), type: 'success'})
          await load()
        } catch (error: any) {
          notification.notify({title: error.response?.data?.message || helper.trans('labels.error'), type: 'error'})
        }
      })
    }

    const handleReattachClick = async (userId: string) => {
      if (!requisitesId.value) {
        return;
      }
      
      try {
        const requisites = await api.getAvailableRequisitesForReattach(requisitesId.value!, userId)
        if (requisites.length === 0) {
          notification.notify({title: helper.trans('labels.no_available_requisites'), type: 'warn'})
        } else {
          reattachPopup.value?.show(userId, requisites)
        }
      } catch (error: any) {
        notification.notify({title: error.response?.data?.message || helper.trans('labels.error'), type: 'error'})
      }
    }

    const handleReattach = async (userId: string, newRequisitesId: number) => {
      if (!requisitesId.value) {
        return;
      }

      await loader.value?.load(async () => {
        try {
          await api.reattachCounterparty(requisitesId.value!, userId, newRequisitesId)
          notification.notify({title: helper.trans('labels.reattach_success'), type: 'success'})
          await load()
        } catch (error: any) {
          notification.notify({title: error.response?.data?.message || helper.trans('labels.reattach_error'), type: 'error'})
        }
      })
    }

    const openBanPopup = (item: CounterpartyStatisticsDTO) => {
      if (requisitesId.value && item.storeHash) {
        banUserPopup.value?.show(requisitesId.value, item.userId, item.storeHash)
      }
    }

    const onBanned = () => {
      load()
    }

    return {
      requisitesId,
      counterparties,
      show,
      hide,
      loader,
      popup,
      dateHelper,
      numberFormatter,
      helper,
      confirmDeleteCounterparty,
      confirmPopup,
      banUserPopup,
      reattachPopup,
      handleReattachClick,
      handleReattach,
      openBanPopup,
      onBanned
    }
  }
})
</script>

